import { Injectable, NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Resolve,
  Router,
  RouterModule,
  Routes,
} from "@angular/router";
import { forkJoin } from "rxjs";
import { map, mergeMap, take, tap } from "rxjs/operators";
import { AccountRelatedDTO } from "../accounts/Account";
import { AccountAppDTO } from "../apps/AccountApp";
import { IsSuperGuard, LoggedInGuard } from "../auth/auth.guards";
import { NotificationService } from "../notifications/notification.service";
import { SessionService } from "../session.service";
import { AccountsSelectionComponent } from "./accounts-selection/accounts-selection.component";
import { InstrumentationService } from "./instrumentation.service";
import { InstrumentationComponent } from "./instrumentation/instrumentation.component";

@Injectable({
  providedIn: "root",
})
export class AccountAppGuard implements CanActivate {
  constructor(private SessionService: SessionService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot) {
    if (Array.isArray(route.queryParams.account)) {
      let queryAccountIDs = Array.isArray(route.queryParams.account)
        ? route.queryParams.account
        : [this.SessionService.currentAccountValue!.accountID];

      queryAccountIDs = queryAccountIDs.map((accountID: string) => {
        return Number.parseInt(accountID);
      });

      const accounts = this.SessionService.accounts!.filter((account) => {
        return queryAccountIDs.includes(account.id) && account.apps.length;
      });

      let accountIDs: any[] = accounts.map((account) => {
        return account.id;
      });

      //Used for All accounts
      if (
        !!route.queryParams.account.find((id: string | number) => id === "all")
      ) {
        accountIDs.push("all");
      }

      if (accountIDs?.length) {
        //Remove all from check
        const tempAccountIDs = [...accountIDs];
        const tempQueryAccountIds = [...queryAccountIDs];
        const allIndex = tempAccountIDs.findIndex((id) => id === "all");
        if (allIndex >= 0) {
          tempAccountIDs.splice(allIndex, 1);
          tempQueryAccountIds.splice(allIndex, 1);
        }

        if (
          tempAccountIDs.sort().toString() ===
          tempQueryAccountIds.sort().toString()
        ) {
          return true;
        } else {
          this.router.navigate(["instrumentation", "view"], {
            queryParams: { ...route.queryParams, account: accountIDs },
          });
          return true;
        }
      }
    } else {
      this.router.navigate(["instrumentation", "view"], {
        queryParams: {
          ...route.queryParams,
          account: [route.queryParams.account],
        },
      });
      return true;
    }

    this.router.navigate(["instrumentation", "accountsSelection"]);
    return false;
  }
}

@Injectable({ providedIn: "root" })
export class StartDateResolve implements Resolve<any> {
  constructor() {}

  resolve(route: ActivatedRouteSnapshot) {
    if (route.queryParams.start) {
      return new Date(route.queryParams.start);
    }
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    return start;
  }
}

@Injectable({ providedIn: "root" })
export class EndDateResolve implements Resolve<any> {
  constructor() {}

  resolve(route: ActivatedRouteSnapshot) {
    if (route.queryParams.end) {
      return new Date(route.queryParams.end);
    }
    return new Date();
  }
}

@Injectable({ providedIn: "root" })
export class CompareDaysResolve implements Resolve<any> {
  constructor() {}

  resolve(route: ActivatedRouteSnapshot) {
    if (route.queryParams.baseline) {
      const options = ["7", "15", "30", "45", "60", "90"];
      if (options.includes(route.queryParams.baseline)) {
        return Number.parseInt(route.queryParams.baseline);
      } else if (route.queryParams.baseline === "industry") {
        return route.queryParams.baseline;
      }
    }
    return 7;
  }
}

@Injectable({ providedIn: "root" })
export class InstrumentsResolve implements Resolve<any> {
  constructor(private InstrumentationService: InstrumentationService) {}

  resolve() {
    return this.InstrumentationService.getInstruments();
  }
}

@Injectable({ providedIn: "root" })
export class AccountAppsInstrumentsResolve implements Resolve<any> {
  constructor(
    private InstrumentationService: InstrumentationService,
    private SessionService: SessionService,
    private NotificationService: NotificationService,
  ) {}

  getAccountAppInstrumentsObs(
    account: AccountRelatedDTO,
    availableInstruments: any,
    startDate: Date,
    endDate: Date,
    compareDays: number,
  ) {
    return this.InstrumentationService.getAccountAppInstruments(
      account.id,
      account.apps[0].id,
    ).pipe(
      mergeMap((accountAppInstruments) => {
        if (!accountAppInstruments?.length) {
          const createAppInstrumentsObs = [];
          let count = 0;

          for (let instrument of availableInstruments) {
            const newAppInstrumentDTO = {
              instrument_id: instrument.id,
              sort_order: count,
            };

            createAppInstrumentsObs.push(
              this.InstrumentationService.createAppInstrument(
                account.id,
                account.apps[0].id,
                newAppInstrumentDTO,
              ),
            );
            count++;
          }

          return forkJoin(createAppInstrumentsObs).pipe(
            mergeMap(() => {
              return this.InstrumentationService.getAccountAppInstruments(
                account.id,
                account.apps[0].id,
              ).pipe(
                mergeMap((appInstrumentations) => {
                  const appInstrumentationObs = appInstrumentations.map(
                    (appInstrumentation) => {
                      return this.InstrumentationService.getAccountAppInstrument(
                        account.id,
                        account.apps[0].id,
                        appInstrumentation.id,
                        startDate,
                        endDate,
                        compareDays,
                      ).pipe(
                        tap((instrumentInfo: any) => {
                          if (instrumentInfo.error) {
                            this.NotificationService.openErrorSnackBar(
                              instrumentInfo.error,
                            );
                          }
                        }),
                        map((instrumentInfo) => {
                          const lhs = appInstrumentation || {};
                          const rhs = instrumentInfo || {};
                          return { ...(lhs as any), ...(rhs as any) };
                        }),
                      );
                    },
                  );

                  return forkJoin(appInstrumentationObs);
                }),
              );
            }),
          );
        } else {
          const appInstrumentationObs = accountAppInstruments.map(
            (appInstrumentation) => {
              return this.InstrumentationService.getAccountAppInstrument(
                account.id,
                account.apps[0].id,
                appInstrumentation.id,
                startDate,
                endDate,
                compareDays,
              ).pipe(
                map((accountAppInstrument) => {
                  const lhs = appInstrumentation || {};
                  const rhs = accountAppInstrument || {};
                  return { ...(lhs as any), ...(rhs as any) };
                }),
                take(1),
              );
            },
          );
          return forkJoin(appInstrumentationObs).pipe(
            map((appInstrumentations) => {
              return { account, appInstrumentations };
            }),
            take(1),
          );
        }
      }),
    );
  }

  resolve(route: ActivatedRouteSnapshot) {
    const availableInstruments = route.parent?.data.instruments;
    const startDate = route.parent?.data.startDate;
    const endDate = route.parent?.data.endDate;
    const compareDays = route.parent?.data.compareDays;

    const accountIDs = route.queryParams.account.map((accountID: string) => {
      return accountID === "all" ? "all" : Number.parseInt(accountID);
    });

    const accounts = this.SessionService.accounts!.filter((account) => {
      return accountIDs.includes(account.id);
    });

    if (accountIDs.includes("all")) {
      accounts.push({
        name: "All Accounts",
        id: "all",
        apps: [{ id: "all" } as any as AccountAppDTO],
      } as any as AccountRelatedDTO);
    }

    const accountAppInstrumentsObsArr = accounts.map((account) => {
      return this.InstrumentationService.getAccountAppInstrumentsObs(
        account,
        availableInstruments,
        startDate,
        endDate,
        compareDays,
        true,
      );
    });

    return forkJoin(accountAppInstrumentsObsArr);
  }
}

const routes: Routes = [
  {
    path: "",
    canActivate: [LoggedInGuard],
    children: [
      {
        path: "",
        data: { sidebar: "admin" },
        canActivate: [IsSuperGuard],
        children: [
          {
            path: "view",
            resolve: {
              startDate: StartDateResolve,
              endDate: EndDateResolve,
              compareDays: CompareDaysResolve,
              instruments: InstrumentsResolve,
            },
            runGuardsAndResolvers: "paramsOrQueryParamsChange",

            canActivate: [AccountAppGuard],
            children: [
              {
                path: "",
                component: InstrumentationComponent,
                resolve: {
                  accountAppInstruments: AccountAppsInstrumentsResolve,
                },
                data: { title: "Instrumentation" },
                runGuardsAndResolvers: "paramsOrQueryParamsChange",
              },
            ],
          },
          {
            path: "accountsSelection",
            data: { title: "Account Selection" },
            component: AccountsSelectionComponent,
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class InstrumentationRoutingModule {}

const instrumentationModuleLazyLoadRoutes: Routes = [
  {
    path: "instrumentation",
    loadChildren: () =>
      import("./instrumentation.module").then((m) => m.InstrumentationModule),
  },
];

export { instrumentationModuleLazyLoadRoutes };
